import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import Form from 'react-bootstrap/Form'
import Page from '../../component/page'
import DiscGolfApp from '../../component/DiscGolfApp'
import GroupsMap from './GroupsMap'
import GroupsTable from './GroupsTable'
import { findByShort as findCountryByShort } from '../../js/countries'
import { findByShort as findStateByShort } from '../../js/states'
import { isAdmin as checkIsAdmin } from '../admin'
import { isHidden } from './visibility'

// Rounds ever recorded, per course, for the whole country.
//
// Its own admin request rather than a field on `/v3/course`: that listing is versioned and
// shipped to installed app builds, and a play count is an admin's number, not a visitor's.
const loadPlayed = (country) => ({
  type: 'ADMIN_LOAD_PLAYED',
  payload: { request: { url: `/admin/curation/played?country=${country}` } },
})

// The courses in the country a visitor is not shown, and why. `/v3/course` returns every one
// of them to an admin with nothing to tell them apart, which is how a page full of merge
// losers and hidden rows ends up looking like the catalogue.
const loadHidden = (country) => ({
  type: 'ADMIN_LOAD_HIDDEN',
  payload: { request: { url: `/admin/curation/hidden?country=${country}` } },
})

function Country({ groups, isAdmin }) {
  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const [country] = useState(() => {
    return findCountryByShort(params.country)
  })
  const [state] = useState(() => {
    return findStateByShort(params.state)
  })
  // Read once, on mount, and deliberately never again: GroupsMap rewrites the URL with
  // ?zoom=&center= on every map movement, so the param is gone the moment you touch the
  // map. What it seeds is a selection, which outlives it in component state.
  const [mergeIds] = useState(() => {
    const raw = searchParams.get('merge')
    if (!raw) return []
    return raw
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isInteger(id) && id > 0)
  })
  const [played, setPlayed] = useState(null)
  // Course id -> `{ active, private, hasVisibleLayouts }`, for the hidden ones
  // only. Null until it answers: until then nothing is known to be hidden, so nothing is.
  const [visibility, setVisibility] = useState(null)
  const [showHidden, setShowHidden] = useState(false)
  // Courses hidden or restored from this page. They stay in the table whatever the
  // toggle says, greyed or not, so a click never makes the row it was made on disappear.
  const [touched, setTouched] = useState(() => new Set())

  // Admin-only, and not merely hidden: a visitor's page must not make the request at all.
  useEffect(() => {
    if (!isAdmin || !country) return
    dispatch(loadPlayed(country.id))
      .then((result) => {
        const rows = JSON.parse(result.payload.data)
        setPlayed(
          rows.reduce((byId, row) => ({ ...byId, [row.courseId]: row.played }), {})
        )
      })
      .catch(() => {})
  }, [dispatch, isAdmin, country])

  // Refetched with the course list, not once: a merge reloads that list, and its losers --
  // still in it, since `/v3/course` gives an admin everything -- are hidden from then on.
  useEffect(() => {
    if (!isAdmin || !country) return
    dispatch(loadHidden(country.id))
      .then((result) => {
        const rows = JSON.parse(result.payload.data)
        setVisibility(
          rows.reduce((byId, { courseId, ...flags }) => ({ ...byId, [courseId]: flags }), {})
        )
      })
      .catch(() => {})
  }, [dispatch, isAdmin, country, groups])

  const onCourseStateChanged = (id, changes) => {
    setVisibility((current) => ({
      ...current,
      [id]: {
        active: true,
        private: false,
        hasVisibleLayouts: true,
        ...(current || {})[id],
        ...changes,
      },
    }))
    setTouched((current) => new Set(current).add(id))
  }

  const onCourseGroupClicked = (group) => {
    console.log('Click group', group)
    navigate(`/course/${group.id}`)
  }

  const renderTitle = () => {
    if (state) {
      return <h1>Courses in {state.name}</h1>
    }
    return <h1>Courses in {country.name}</h1>
  }

  const countryGroups = groups.filter((group) => {
    if (group.country === 236) {
      return group.country === country.id && group.state === state.id
    }
    return group.country === country.id
  })

  const hiddenCount = isAdmin
    ? countryGroups.filter((group) => isHidden(visibility, group.id)).length
    : 0
  const filteredGroups =
    showHidden || hiddenCount === 0
      ? countryGroups
      : countryGroups.filter(
          (group) =>
            !isHidden(visibility, group.id) ||
            touched.has(group.id) ||
            // A ?merge= link names its pair explicitly; hiding one of them would leave half a merge.
            mergeIds.includes(group.id)
        )

  return (
    <Page className="container country">
      <div className="row">
        <div className="col-sm-12">
          {renderTitle()}
          <GroupsMap
            country={country}
            state={state}
            groups={filteredGroups.filter((group) => group.location)}
            onCourseGroupClicked={onCourseGroupClicked}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="hidden-xs padme">
            {/* <?php include_once TEMPLATES_PATH . '/adsense.tall.html'; ?> */}
          </div>
          <h2>Courses</h2>
          {isAdmin && hiddenCount > 0 && (
            <Form.Check
              type="switch"
              id="show-hidden-courses"
              className="mb-2"
              label={`Show hidden courses (${hiddenCount})`}
              checked={showHidden}
              onChange={(event) => setShowHidden(event.target.checked)}
            />
          )}
          <GroupsTable
            groups={filteredGroups}
            onCourseGroupClicked={onCourseGroupClicked}
            // Ids that are not on this page are dropped rather than carried: a selection
            // holding a course the table cannot show is one you can neither see nor undo.
            initialSelected={
              isAdmin
                ? mergeIds.filter((id) => filteredGroups.some((g) => g.id === id))
                : []
            }
            played={played}
            visibility={isAdmin ? visibility : null}
            onCourseStateChanged={onCourseStateChanged}
          />
          <h2>Where do the courses come from?</h2>
          <p>
            The courses you see here have been created using the <DiscGolfApp />
            . The database is provided as a free service for anyone either
            visiting this webpage or using the <DiscGolfApp />. All courses are
            freely shared in the hope that players will maintain course
            information and share updates of their favourite courses for{' '}
            <i>everyones</i> benefit.
          </p>
          <h2>How we handle courses</h2>
          <p>
            Courses consist of one or more layouts, which may change over time.
            In 2022 we've changed the policy on how course versions are handled,
            to be able to show both courses and their different layouts. We have
            tried to merge layouts with the same name into courses and in a few
            cases even layouts with different names beloing to the same course.
            This is a manual process, so if you think some courses/layouts
            belong together you can contact us with names (please also include
            country and state) of the courses and we'll try and merge then as
            well.
          </p>

          <h2>Can't find my course or the information is incorrect</h2>
          <p>
            You can serch for the course by name at the top of this page. If you
            still can't find it then chances are that the course you are looking
            for hasn't been added into our system yet. You can create new
            courses and layouts yourself using the <DiscGolfApp />.
          </p>
        </div>
      </div>
    </Page>
  )
}

const mapStateToProps = (state) => {
  const { data: groups } = state.groups
  return {
    groups,
    isAdmin: checkIsAdmin(state.auth.user),
  }
}

export default connect(mapStateToProps)(Country)
